import { useCallback } from 'react'
import { IRowNode } from 'ag-grid-enterprise'
import { CustomReportVariable, RowType } from '../../../../redux/context/customReports/typesVariable'

export interface UseRowFilteringReturn {
  isExternalFilterPresent: () => boolean
  doesExternalFilterPass: (node: IRowNode<CustomReportVariable>) => boolean
}

/**
 * Hook to filter empty rows in custom reports using AG Grid's external filter API
 * @param displayEmptyRows - Whether to display rows without data
 * @returns External filter functions for AG Grid
 */
export const useRowFiltering = (displayEmptyRows: boolean): UseRowFilteringReturn => {
  const isExternalFilterPresent = useCallback(() => {
    return !displayEmptyRows
  }, [displayEmptyRows])

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<CustomReportVariable>) => {
      if (!node.data) return false

      // If row has explicit showEmptyRows flag, always show it
      if (node.data.params?.showEmptyRows) {
        return true
      }

      // Empty rows (RowType.empty) are always hidden when filtering is active
      if (node.data.rowType === RowType.empty) {
        return false
      }

      // Title rows without children are hidden when filtering is active
      if (node.data.rowType === RowType.title && !node.data.children?.length) {
        return false
      }

      // Check if row has any non-zero balance values
      const balances = node.data.balances || []
      for (const balance of balances) {
        const value = balance?.value ?? 0
        if (!Number.isNaN(value) && value !== 0) {
          return true // Found at least one non-empty value
        }
      }

      // No non-zero values found, hide the row
      return false
    },
    [displayEmptyRows]
  )

  return {
    isExternalFilterPresent,
    doesExternalFilterPass
  }
}
